import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BuildingUseTypes,
  CarbonFactorDataSources,
  ConstructionTypes,
  Ecom,
  Email,
  Firm,
  FoundationTypes,
  HorizontalGravitySystems,
  LateralSystems,
  LcaProjectPhases,
  LcaTools,
  MaterialCategories,
  MaterialTypes,
  MaterialQuantityDataSources,
  Podium,
  Project,
  RiskCategories,
  SeismicDesignCategories,
  SoilBearingPressure,
  User,
  UserAgreement,
  VerticalGravitySystems,
 } from '../types/types';
import { RootState } from '../store/store';
import { AssessmentStageType, DataPointType, HeatMapType, IntensityMetricType, MaterialBoxPlotType } from "components/charts/types";
import { BoxPlotType } from "components/charts/types";

const baseUrl = process.env.REACT_APP_BACKEND_API;

interface UserData {
  auth0_id: string;
  site_role: string;
  is_enabled: boolean;
}

export interface VisBoxwhiskersOptions {
  visualization: Omit<BoxPlotType | HeatMapType, MaterialBoxPlotType>;
  assessment_stage: AssessmentStageType;
  assessment_year_start?: number;
  assessment_year_end?: number;
  response_data_type: 'array' | 'dictionary';
  data_point_type: DataPointType;
};

export interface VisBreakdownOptions {
  visualization: 'Element' | 'Material';
  material: 'Concrete' | 'Steel' | 'Timber' | null;
  intensity_metric: IntensityMetricType;
  area_unit: 'ft' | 'm';
  assessment_year_start?: number;
  assessment_year_end?: number;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}`,
    prepareHeaders: async (headers, { getState }) => {
      // Skip waiting for auth token for endpoints that don't need one like /privacy_policy
      if (headers.get('Skip-Auth') === 'true') {
        headers.delete('Skip-Auth');
        return new Promise((resolve) => resolve(headers));
      }

      return new Promise((resolve) => {
        let attempts = 0;
        const maxRetries = 5; // Maximum retry attempts
        const retryInterval = 500; // Minimum time interval between retries in milliseconds

        function checkToken() {
          const token = (getState() as RootState).auth.token;
          attempts++;

          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
            resolve(headers);
          } else if (attempts < maxRetries) {
            const intervalMultiplier = 2 ** (attempts - 1); // Exponential timeout backoff
            setTimeout(checkToken, retryInterval * intervalMultiplier);
          } else {
            console.log(`Max retries reached (${maxRetries}). Proceeding without authorization.`);
            resolve(headers);
          }
        }

        checkToken();
      });
    },
  }),
  endpoints: (builder) => ({
    getFirms: builder.query<Firm[], void>({
      query: () => 'firms',
      //@ts-ignore
      providesTags: (result) =>
        result
          ?
            [...result.map(({ name }) => ({ type: 'Firm', name } as const)), { type: 'Firms', id: 'LIST' }]
          :
            [{ type: 'Firms', id: 'LIST' }],
    }),
    getUser: builder.query<User, void>({
      query: () => `userinfo`,
    }),
    getProjectDashCounts: builder.query<any, void>({
      query: () => 'projects/count_summary/',
    }),
    getUsers: builder.query<User[], void>({
      query: () => 'users',
      //@ts-ignore
      providesTags: (result) =>
        // is result available?
        result
        ? // successful query
          [
            ...result.map(({ auth0_id }) => ({ type: 'Users', auth0_id } as const)),
            { type: 'Users', id: 'LIST' },
          ]
        : // an error occurred / no users are returned, but we still want to refetch this query when `{ type: 'Users', id: 'LIST' }` is invalidated
          [{ type: 'Users', id: 'LIST' }],
    }),
    updateUser: builder.mutation<void, { id: string; formData: Omit<User, 'auth0_id' | 'site_role' | 'is_enabled'> }>({
      query: ({ id, formData }) => {
        return {
          url: `users/${id}/`,
          method: 'PATCH',
          body: formData,
        }
      },
      //@ts-ignore
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    exportUser: builder.query({
      query: (site_role: string) => ({
        url: `/users/export/?site_role=${site_role}`,
        responseHandler: (response) => response.blob(),
      }),
    }),
    deleteUser: builder.mutation<void, number>({
      query: (auth0_id) => ({
        url: `/users/${auth0_id}/`,
        method: 'DELETE',
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Users', id: 'LIST' }]
    }),
    getVisBoxwhisker: builder.mutation<any, VisBoxwhiskersOptions>({
      query: (visOptions) => ({
        url: 'projects/vis_boxwhisker/',
        method: 'POST',
        body: visOptions,
      }),
    }),
    getVisBreakdown: builder.mutation<any, VisBreakdownOptions>({
      query: (visOptions) => ({
        url: 'projects/vis_breakdown/',
        method: 'POST',
        body: visOptions,
      }),
    }),
    getEcom: builder.query<Ecom[], void>({
      query: () => 'ecom',
      //@ts-ignore
      providesTags: (result) =>
        result
          ? [...result.map(({ version }) => ({ type: 'Ecom', version } as const)), { type: 'Ecom', id: 'LIST' }]
          : [{ type: 'Ecom', id: 'LIST' }],
    }),
    getEcomLatest: builder.query<Ecom[], void>({
      query: () => 'ecom/active',
      //@ts-ignore
      providesTags: (result) =>
        result
          ? [{ type: 'Ecom', version: (result as any).version }, { type: 'Ecom', id: 'LIST' }]
          : [{ type: 'Ecom', id: 'LIST' }],
    }),
    getEcomVersions: builder.query<number[], void>({
      query: () => 'ecom/versions/',
    }),
    getMaterialTypes: builder.query<MaterialTypes[], void>({
      query: () => 'material_types',
    }),
    getUserAgreement: builder.query<UserAgreement, void>({
      //@ts-ignore
      queryFn: (_arg, _api, _extraOptions, baseQuery) => {
        return baseQuery({ url: 'user_agreement', headers: { 'Skip-Auth': 'true' } });
      },
      //@ts-ignore
      providesTags: ['UserAgreement']
    }),
    getPrivacyPolicy: builder.query<any, void>({
      //@ts-ignore
      queryFn: (_arg, _api, _extraOptions, baseQuery) => {
        return baseQuery({ url: 'privacy_policy', headers: { 'Skip-Auth': 'true' } });
      },
      //@ts-ignore
      providesTags: ['PrivacyPolicy']
    }),
    getPrimaryBuildingUseTypes: builder.query<BuildingUseTypes[], void>({
      query: () => 'building_use_types',
    }),
    getConstructionTypes: builder.query<ConstructionTypes[], void>({
      query: () => 'construction_types',
    }),
    getRiskCategories: builder.query<RiskCategories[], void>({
      query: () => 'risk_categories',
    }),
    getSeismicDesignCategories: builder.query<SeismicDesignCategories[], void>({
      query: () => 'seismic_design_categories',
    }),
    getHorizontalGravitySystems: builder.query<HorizontalGravitySystems[], void>({
      query: () => 'horizontal_gravity_systems',
    }),
    getVerticalGravitySystems: builder.query<VerticalGravitySystems[], void>({
      query: () => 'vertical_gravity_systems',
    }),
    getLateralSystems: builder.query<LateralSystems[], void>({
      query: () => 'lateral_systems',
    }),
    getPodium: builder.query<Podium[], void>({
      query: () => 'podium',
    }),
    getFoundationTypes: builder.query<FoundationTypes[], void>({
      query: () => 'foundation_types',
    }),
    getMaterialCategories: builder.query<MaterialCategories[], void>({
      query: () => 'material_categories',
    }),
    getSoilBearingPressure: builder.query<SoilBearingPressure[], void>({
      query: () => 'allowable_soil_bearing_pressure',
    }),
    getMaterialQuantityDataSources: builder.query<MaterialQuantityDataSources[], void>({
      query: () => 'material_quantity_data_sources',
    }),
    getCarbonFactorDataSources: builder.query<CarbonFactorDataSources[], void>({
      query: () => 'carbon_factor_data_sources',
    }),
    getLcaProjectPhases: builder.query<LcaProjectPhases[], void>({
      query: () => 'lca_project_phases',
    }),
    getLcaTools:builder.query<LcaTools[], void>({
      query: () => 'lca_tools',
    }),
    getProjects: builder.query<Project[], void>({
      query: () => 'projects',
      //@ts-ignore
      providesTags: (result) =>
        result
          ?
            [...result.map(({ id }) => ({ type: 'Firm', id } as const)), { type: 'Projects', id: 'LIST' }]
          :
            [{ type: 'Projects', id: 'LIST' }],
    }),
    getProjectDetails: builder.query<Project, Number>({
      query: (project_id) => `projects/${project_id}/`,
    }),
    getProjectsExport: builder.query({
      query: () => ({
        url: '/projects/export_projects',
        responseHandler: response => response.blob(),
      }),
    }),
    getComplianceExport: builder.query({
      query: () => ({
        url: '/projects/export_compliance',
        responseHandler: response => response.blob()
      }),
    }),
    createProject: builder.mutation<Project, Omit<Project, 'id' | 'updated_at' | 'submitted_at' | 'created_at' | 'is_submitted' | 'is_my_project'>>({
      query: (projectData: Project) => ({
        url: `projects/`,
        method: 'POST',
        body: projectData,
      }),
    }),
    // Partial by design - each wizard step PATCHes only the fields it owns (see
    // lib/utils/projectPatch.ts).
    updateProject: builder.mutation<Project, Partial<Project> & { id: number }>({
      query: (projectData) => {
        // Destructures projectData to exclude the id field
        const { id, ...dataToUpdate } = projectData;
        return {
          url: `projects/${id}/`,
          method: 'PATCH',
          body: dataToUpdate, 
        };
      },
      //@ts-ignore
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }],
      // Keep the cached project detail in step with what the server just stored. Without
      // this, re-opening a project in the same session can serve a pre-save row - which is
      // how a submitted project could be reverted to Draft (Entry ID 12 Bug 2), and why a
      // saved edit could appear to have been lost.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Only the save is allowed to fail quietly here - its error is surfaced to the
        // user elsewhere. Keep the dispatch outside the catch so a genuine fault in the
        // cache write (renamed endpoint, RTK change) is not swallowed with it.
        const saved = await queryFulfilled.catch(() => null);

        if (!saved) return;

        dispatch(api.util.upsertQueryData('getProjectDetails', arg.id, saved.data));
      },
    }),
    uploadProject: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: 'projects/upload/',
        method: 'POST',
        body: formData,
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
    deleteProject: builder.mutation<void, number>({
      query: (project_id) => ({
        url: `/projects/${project_id}`,
        method: 'DELETE',
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
    uploadFirm: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: 'firms/upload/',
        method: 'POST',
        body: formData,
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Firms', id: 'LIST' }]
    }),
    createFirm: builder.mutation<void, any>({
      query: (formData: any) => {
        const body: Firm = {
          name: formData.name,
          address: formData.address,
          country: formData.country,
          subnational:  formData.subnational,
          city: formData.city,
          zipcode: formData.zipcode,
          website: formData.website,
        }
        return {
          url: 'firms/',
          method: 'POST',
          body,
        }
      },
      //@ts-ignore
      invalidatesTags: [{ type: 'Firms', id: 'LIST' }],
    }),
    updateFirm: builder.mutation<void, { id: string; formData: any }>({
      query: ({ id, formData }) => {
        return {
          url: `firms/${id}/`,
          method: 'PATCH',
          body: formData,
        }
      },
      //@ts-ignore
      invalidatesTags: [{ type: 'Firms', id: 'LIST' }],
    }),
    createUser:builder.mutation<void, Omit<User, 'auth0_id' | 'is_enabled'> & { zipcode: string | null }>({
      query: user => ({
        url: `users/`,
        method: 'POST',
        body: {
        firm: user.firm,
        address: user.address,
        country: user.country,
        subnational: user.subnational,
        city: user.city,
        zipcode: user.zipcode,
        site_role: user.site_role,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        is_enabled: true,
      },
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    deleteFirm: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `firms/${id}/`,
        method: 'DELETE',
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Firms', id: 'LIST' }],
    }),
    createMassEmail: builder.mutation({
      query: (emailData: Email) => ({
        url: `email/`,
        method: 'POST',
        body: emailData
      })
    }),
    createEcom: builder.mutation<void, Omit<Ecom, 'created_by' | 'created_at' | 'is_active'>>({
      query: (ecomData: Ecom) => ({
        url: `ecom/`,
        method: 'POST',
        body: ecomData,
      }),
    //@ts-ignore
    invalidatesTags: [{ type: 'Ecom', id: 'LIST' }],
    }),
    setActiveEcom: builder.mutation<void, number>({
      query: (version_id) => ({
        url: `ecom/${version_id}/set_active/`,
        method: 'POST',
      }),
    //@ts-ignore
    invalidatesTags: (version_id) => [{ type: 'Ecom', version: version_id }],
    }),
    deleteEcom: builder.mutation<void, number>({
      query: (version_id) => ({
        url: `/ecom/${version_id}`,
        method: 'DELETE',
      }),
    //@ts-ignore
    invalidatesTags: (version_id) => [{ type: 'Ecom', version: version_id }, { type: 'Ecom', id: 'LIST' }],
    }),
    updateRole: builder.mutation<void, Omit<UserData, 'is_enabled'>>({
      query: user => ({
        url: `users/${user.auth0_id}/`,
        method: 'PATCH',
        body: {site_role : user.site_role},
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    updateStatus: builder.mutation<void, Omit<UserData, 'site_role'>>({
      query: user => ({
        url: `users/${user.auth0_id}/`,
        method: 'PATCH',
        body: { is_enabled: user.is_enabled },
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    updateUserAgreement: builder.mutation<void, string>({
      query: (markdownText) => ({
        url: 'user_agreement/',
        method: 'PATCH',
        body: { text: markdownText },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      //@ts-ignore
      invalidatesTags: ['UserAgreement']
    }),
    updatePrivacyPolicy: builder.mutation<void, string>({
      query: (markdownText) => ({
        url: 'privacy_policy/',
        method: 'PATCH',
        body: { text: markdownText },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      //@ts-ignore
      invalidatesTags: ['PrivacyPolicy'],
    }),
    updateUserProfile: builder.mutation<void, Omit<UserData, 'site_role' | 'is_enabled' | 'email' | 'firm'>>({
      query: user => ({
        url: `userinfo/`,
        method: 'PATCH',
        body: user,
      }),
      //@ts-ignore
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
  }),
});

export const { useGetFirmsQuery, useUpdateFirmMutation, useDeleteFirmMutation, useLazyGetFirmsQuery, useGetUserQuery, useUpdateUserMutation, useDeleteUserMutation, useLazyGetUsersQuery, useGetUsersQuery, useLazyExportUserQuery, useGetEcomQuery, useGetEcomLatestQuery, useGetEcomVersionsQuery,
  useGetMaterialTypesQuery, useLazyGetProjectsQuery, useLazyGetProjectDashCountsQuery, useGetPrivacyPolicyQuery,useGetUserAgreementQuery, useGetPrimaryBuildingUseTypesQuery, useGetConstructionTypesQuery, 
  useGetRiskCategoriesQuery, useGetSeismicDesignCategoriesQuery, useGetHorizontalGravitySystemsQuery, useGetVerticalGravitySystemsQuery, 
  useGetLateralSystemsQuery, useGetPodiumQuery, useGetFoundationTypesQuery, useGetMaterialCategoriesQuery, useGetSoilBearingPressureQuery, useGetMaterialQuantityDataSourcesQuery, useGetCarbonFactorDataSourcesQuery, useGetLcaProjectPhasesQuery, useGetLcaToolsQuery, 
  useGetProjectsQuery, useGetProjectDetailsQuery, useLazyGetProjectsExportQuery,useGetProjectsExportQuery, useLazyGetComplianceExportQuery, useGetComplianceExportQuery, useCreateProjectMutation, useUpdateProjectMutation, useUploadProjectMutation, useDeleteProjectMutation, useUploadFirmMutation, 
  useCreateFirmMutation, useCreateUserMutation, useCreateMassEmailMutation, useCreateEcomMutation, useSetActiveEcomMutation, useDeleteEcomMutation, 
  useUpdateRoleMutation, useUpdateStatusMutation, useUpdateUserAgreementMutation, useUpdatePrivacyPolicyMutation, useUpdateUserProfileMutation, useGetVisBoxwhiskerMutation, useGetVisBreakdownMutation } = api;

export default api;